import { z } from 'zod'
import { LOCAL_DATE_RE } from './dates.js'
import type { Catalog } from './fold.js'
import {
  ASSET_ID_RE,
  BADGE_IDS,
  CONTENT_ID_RE,
  GAME_IDS,
  LESSON_IDS,
  LESSON_REGION,
  type LessonId,
  SLOTS,
  STATIC_RECAP_QUESTION_IDS,
} from './ids.js'

/**
 * Schemas for everything under content/. `pnpm content:check` parses the real files with these
 * and runs the cross file rules below, so an unknown id anywhere fails the build (CONTRACTS §1).
 */

const contentId = z.string().regex(CONTENT_ID_RE, 'content id: lowercase, digits and dashes')
const assetId = z.string().regex(ASSET_ID_RE, 'asset id: lowercase path segments')
const trText = z.string().min(1)

const modeText = z.strictObject({ mini: trText, core: trText })

export const LessonCardSchema = z.strictObject({
  id: z.string().regex(/^c\d+$/),
  visual: assetId,
  anim: contentId,
  text: modeText,
  voice: z.strictObject({ mini: contentId, core: contentId }),
})

export const LessonQuestionSchema = z.strictObject({
  id: z.string().min(1).max(8),
  prompt: modeText,
  options: z
    .array(
      z.strictObject({
        id: z.string().min(1).max(8),
        text: trText,
        visual: assetId.optional(),
        correct: z.boolean(),
      }),
    )
    .min(2)
    .max(3),
  miniOptions: z.array(z.string()).length(2),
  hint: trText,
  voice: z.strictObject({ prompt: contentId, hint: contentId }),
})

export const LessonSchema = z.strictObject({
  id: z.enum(LESSON_IDS),
  /** The file keeps the CONTENT §2 slug (p1-firca) while the id is the CONTRACTS §1 id (p1). */
  slug: contentId,
  region: z.number().int().min(1).max(6),
  title: trText,
  faculty: z.enum(['pedodonti', 'ortodonti']),
  intro: trText,
  cards: z.array(LessonCardSchema),
  question: LessonQuestionSchema.nullable(),
  reward: z.strictObject({ badge: contentId, cosmetic: contentId }),
  /** Card ids still waiting for the partner dentist (CONTENT `dr` marker). */
  dr: z.array(z.string()),
})
export type Lesson = z.infer<typeof LessonSchema>

export const FoodSchema = z.strictObject({
  id: contentId,
  name: trText,
  class: z.enum(['friend', 'risky']),
  asset: assetId,
})
export const FoodsSchema = z.strictObject({
  dr: z.boolean(),
  items: z.array(FoodSchema).min(1),
})
export type Food = z.infer<typeof FoodSchema>

export const HabitSchema = z.strictObject({
  id: contentId,
  /** The accessible name of the card, also the Doktor Kunduz correction line's subject. */
  label: trText,
  answer: z.boolean(),
  asset: assetId,
})
export const HabitsSchema = z.strictObject({
  dr: z.boolean(),
  cards: z.array(HabitSchema).min(1),
})
export type Habit = z.infer<typeof HabitSchema>

export const BadgeSchema = z.strictObject({
  id: contentId,
  name: trText,
  /** One line shown under a locked silhouette. */
  hint: trText,
  icon: assetId,
})
export const BadgesSchema = z.strictObject({ badges: z.array(BadgeSchema) })
export type Badge = z.infer<typeof BadgeSchema>

export const ShopItemSchema = z.strictObject({
  id: contentId,
  name: trText,
  kind: z.enum(['food', 'cosmetic', 'decor']),
  slot: z.enum(SLOTS).optional(),
  /** null for anything that is earned rather than bought. */
  price: z.number().int().min(1).max(2000).nullable(),
  source: z.enum(['shop', 'region', 'plus']),
  asset: assetId,
})
export const AnchorSchema = z.strictObject({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  scale: z.number().min(0.05).max(3),
})
export const ShopSchema = z.strictObject({
  items: z.array(ShopItemSchema),
  /** Cosmetic anchor points per pet stage, as fractions of the sprite box (ASSETS §3.2). */
  anchors: z.record(z.string(), z.record(z.string(), AnchorSchema)),
})
export type ShopItem = z.infer<typeof ShopItemSchema>
export type Shop = z.infer<typeof ShopSchema>

export type CopyTree = { [key: string]: string | CopyTree }
export const CopyTreeSchema: z.ZodType<CopyTree> = z.lazy(() =>
  z.record(z.string(), z.union([z.string(), CopyTreeSchema])),
)

export const CoachSchema = z.strictObject({
  dr: z.boolean(),
  cues: z.array(
    z.strictObject({
      t: z.number().int().min(0).max(120),
      zone: z.enum(['start', 'sag-ust', 'sol-ust', 'sol-alt', 'sag-alt', 'end']),
      segment: z.enum(['dis', 'ic', 'cigneme', 'none']),
      text: trText,
      voice: contentId,
    }),
  ),
  encouragement: z.array(trText),
  faceLost: trText,
})

export const SongSchema = z.strictObject({
  dr: z.boolean(),
  bpm: z.number().int().min(60).max(200),
  lines: z.array(z.strictObject({ t: z.number().min(0).max(120), text: trText, voice: contentId })),
})

export const GameCopySchema = z.strictObject({
  id: z.enum(GAME_IDS),
  /** The short name the görev copy fills into `{{game}}` (W3-OVERHAUL §4.2). */
  name: trText,
  intro: trText,
  hint: trText,
  /** One non question sentence linking the play to real care (CONTRACTS §5). */
  reflection: trText,
})

/* ------------------------------------------------------------------ recap, görev, daily */

/** Hatırlatma Eğitimi cards: c0 is Doktor Kunduz's opener, c1..c6 one per lesson in order. */
export const RecapCardSchema = LessonCardSchema.extend({
  lessonId: z.enum(LESSON_IDS).optional(),
})

/** A recap question is a lesson question with its own id family and the lesson it revisits. */
export const RecapQuestionSchema = LessonQuestionSchema.extend({
  id: z.enum(STATIC_RECAP_QUESTION_IDS),
  lessonId: z.enum(LESSON_IDS),
})
export type RecapQuestion = z.infer<typeof RecapQuestionSchema>

export const RecapSchema = z.strictObject({
  id: z.literal('hatirlatma'),
  title: trText,
  intro: trText,
  cards: z.array(RecapCardSchema).length(1 + LESSON_IDS.length),
  questions: z.array(RecapQuestionSchema).length(LESSON_IDS.length),
  reward: z.strictObject({ badge: contentId }),
  cooldownDays: z.number().int().min(1).max(30),
  dr: z.array(z.string()),
})
export type Recap = z.infer<typeof RecapSchema>

const gorevText = modeText

export const ChallengesSchema = z.strictObject({
  brush: z
    .array(
      z.strictObject({
        id: z.enum(['g-brush-sabah', 'g-brush-aksam', 'g-brush-tamgun']),
        window: z.enum(['morning', 'evening', 'both']),
        text: gorevText,
        icon: assetId,
      }),
    )
    .min(3),
  question: z.strictObject({
    id: z.literal('g-soru'),
    text: gorevText,
    icon: assetId,
    fallback: z.strictObject({ id: z.literal('g-ders'), text: gorevText }),
  }),
  game: z.array(z.strictObject({ id: z.literal('g-oyun'), text: gorevText, icon: assetId })).min(1),
  uclu: z.strictObject({ text: gorevText }),
})
export type Challenges = z.infer<typeof ChallengesSchema>

/**
 * The six key messages the daily loop may rephrase (W3-OVERHAUL §2.6), one per lesson in
 * `LESSON_IDS` order. `docs/prompts/daily.md` carries the wording; a daily question names
 * its fact and nothing outside this list passes.
 */
export const FACT_IDS = [
  'iki-kez-iki-dakika',
  'su-ve-meyve',
  'alti-yas-disi',
  'parmak-emme-birakilir',
  'nefes-burundan',
  'dil-damakta',
] as const
export type FactId = (typeof FACT_IDS)[number]
export const FACT_LESSON: Record<FactId, LessonId> = {
  'iki-kez-iki-dakika': 'p1',
  'su-ve-meyve': 'p2',
  'alti-yas-disi': 'p3',
  'parmak-emme-birakilir': 'o1',
  'nefes-burundan': 'o2',
  'dil-damakta': 'o3',
}

/** A generated question: text options only, no pictures and no rendered voice. */
export const DailyQuestionSchema = z.strictObject({
  id: z.string().regex(/^d-\d{4}-\d{2}-\d{2}-[1-6]$/, 'daily question id'),
  lessonId: z.enum(LESSON_IDS),
  factId: z.enum(FACT_IDS),
  prompt: modeText,
  options: z
    .array(z.strictObject({ id: z.string().min(1).max(8), text: trText, correct: z.boolean() }))
    .length(3),
  miniOptions: z.array(z.string()).length(2),
  hint: trText,
})
export type DailyQuestion = z.infer<typeof DailyQuestionSchema>

export const DailySchema = z.strictObject({
  date: z.string().regex(LOCAL_DATE_RE, 'YYYY-MM-DD'),
  /** Doktor Kunduz's line of the day on the Bugün card. */
  tip: z.strictObject({ mini: trText, core: trText, voice: contentId.optional() }),
  questions: z.array(DailyQuestionSchema).length(LESSON_IDS.length),
  /** A playful rename of the day's game target; it never changes a number. */
  gorevTwist: z.strictObject({ text: modeText, kind: z.literal('game') }).optional(),
})
export type Daily = z.infer<typeof DailySchema>

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z]+)\s*\}\}/g
const ALLOWED_PLACEHOLDERS = new Set(['game', 'score'])

/* ------------------------------------------------------------------ copy rules */

/** CONVENTIONS §4, the hard Turkish rule, checked mechanically over every content string. */
const BANNED_COPY: { pattern: RegExp; why: string }[] = [
  { pattern: /[—–]/, why: 'em dash or en dash' },
  { pattern: /sadece[^.!?]{0,80}değil,\s*aynı zamanda/i, why: '"sadece ... değil, aynı zamanda"' },
  { pattern: /unutmayın ki/i, why: '"unutmayın ki"' },
  { pattern: /haydi keşfedelim/i, why: '"haydi keşfedelim"' },
  { pattern: /harika bir yolculuk/i, why: '"harika bir yolculuk"' },
  {
    pattern: /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u,
    why: 'emoji inside copy (an icon slot in the UI is fine)',
  },
]

export type CopyProblem = { path: string; why: string; text: string }

export function checkCopyString(text: string, path: string): CopyProblem[] {
  return BANNED_COPY.filter((rule) => rule.pattern.test(text)).map((rule) => ({
    path,
    why: rule.why,
    text,
  }))
}

export function walkStrings(value: unknown, path: string, visit: (s: string, p: string) => void) {
  if (typeof value === 'string') {
    visit(value, path)
    return
  }
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) walkStrings(item, `${path}[${index}]`, visit)
    return
  }
  if (value && typeof value === 'object') {
    for (const [key, child] of Object.entries(value)) {
      walkStrings(child, path ? `${path}.${key}` : key, visit)
    }
  }
}

/* ------------------------------------------------------------------ bundle */

export type RawContent = {
  lessons: Record<string, unknown>
  foods: unknown
  habits: unknown
  badges: unknown
  shop: unknown
  copy: unknown
  parentCopy: unknown
  copyEn: unknown
  /** W3: absent until W3-1 lands the files; present means validated. */
  recap?: unknown
  challenges?: unknown
  /** `content/daily/<date>.json` keyed by file name. */
  daily?: Record<string, unknown>
}

export type ContentIssue = { severity: 'error' | 'pending'; where: string; message: string }

/** Copy keys CONTENT §6 declares canonical. Missing one is an error, not a warning. */
export const REQUIRED_COPY_KEYS = [
  'yuva.brush.morning',
  'yuva.brush.evening',
  'yuva.brush.extra',
  'yuva.brush.done',
  'yuva.bubble.parlak',
  'yuva.bubble.normal',
  'yuva.bubble.mat',
  'yuva.bubble.uzgun',
  'yuva.feedFull',
  'yuva.gameCap',
  'yuva.nightEvent',
  'yuva.emptyInventory',
  'result.verified',
  'result.tamGun',
  'result.partial',
  'result.triedMorning',
  'result.triedEvening',
  'result.extra',
  'result.nocam',
  'result.freezeUsed',
  'camera.explain.title',
  'camera.explain.body',
  'camera.explain.allow',
  'camera.explain.without',
  'camera.explain.denied',
  'quiz.correctFirst',
  'quiz.correctLater',
  'quiz.wrong',
  'quiz.lessonDone',
  'error.generic',
  'error.camera',
  'error.assetFailed',
]

export const REQUIRED_PARENT_COPY_KEYS = [
  'gate.title',
  'gate.subtitle',
  'gate.adultCheck',
  'observation.disclaimer',
  'observation.nudge',
  'onboarding.cameraPromise',
  'onboarding.handoff',
  'sync.synced',
  'sync.pending',
  'sync.error',
  'account.delete',
  'account.export',
  'reminder.morning',
  'reminder.evening',
  'reminder.nudge',
]

function lookup(tree: unknown, dotted: string): unknown {
  let node: unknown = tree
  for (const key of dotted.split('.')) {
    if (!node || typeof node !== 'object') return undefined
    node = (node as Record<string, unknown>)[key]
  }
  return node
}

export function validateContent(raw: RawContent): ContentIssue[] {
  const issues: ContentIssue[] = []
  const error = (where: string, message: string) =>
    issues.push({ severity: 'error', where, message })
  const pending = (where: string, message: string) =>
    issues.push({ severity: 'pending', where, message })

  const parse = <T>(schema: z.ZodType<T>, value: unknown, where: string): T | null => {
    const result = schema.safeParse(value)
    if (result.success) return result.data
    for (const problem of result.error.issues) {
      error(where, `${problem.path.join('.') || '(root)'}: ${problem.message}`)
    }
    return null
  }

  const lessons: Lesson[] = []
  for (const [file, value] of Object.entries(raw.lessons)) {
    const lesson = parse(LessonSchema, value, `lessons/${file}`)
    if (lesson) lessons.push(lesson)
  }

  const ids = lessons.map((lesson) => lesson.id)
  if (ids.length !== LESSON_IDS.length || LESSON_IDS.some((id) => !ids.includes(id))) {
    error('lessons', `expected exactly ${LESSON_IDS.join(', ')}, found ${ids.join(', ') || 'none'}`)
  }
  const pedodonti = lessons.filter((lesson) => lesson.faculty === 'pedodonti').length
  const ortodonti = lessons.filter((lesson) => lesson.faculty === 'ortodonti').length
  if (lessons.length === LESSON_IDS.length && (pedodonti !== 3 || ortodonti !== 3)) {
    error('lessons', `EDU-01 wants three lessons per faculty, found ${pedodonti} and ${ortodonti}`)
  }
  for (const lesson of lessons) {
    if (LESSON_REGION[lesson.id] !== lesson.region) {
      error(`lessons/${lesson.id}`, `region must be ${LESSON_REGION[lesson.id]}`)
    }
    if (lesson.cards.length === 0)
      pending(`lessons/${lesson.id}`, 'cards are empty, WP-B fills them')
    if (lesson.question === null) {
      pending(`lessons/${lesson.id}`, 'question is missing, WP-B fills it')
      continue
    }
    const correct = lesson.question.options.filter((option) => option.correct)
    if (correct.length !== 1) {
      error(
        `lessons/${lesson.id}`,
        `EDU-01 wants exactly one correct option, found ${correct.length}`,
      )
    }
    const optionIds = new Set(lesson.question.options.map((option) => option.id))
    for (const miniOption of lesson.question.miniOptions) {
      if (!optionIds.has(miniOption)) {
        error(`lessons/${lesson.id}`, `miniOptions references unknown option ${miniOption}`)
      }
    }
  }

  const foods = parse(FoodsSchema, raw.foods, 'foods.json')
  const habits = parse(HabitsSchema, raw.habits, 'habits.json')
  const badges = parse(BadgesSchema, raw.badges, 'badges.json')
  const shop = parse(ShopSchema, raw.shop, 'shop.json')
  parse(CopyTreeSchema, raw.copy, 'copy/tr.json')
  parse(CopyTreeSchema, raw.parentCopy, 'copy/parent.tr.json')
  parse(CopyTreeSchema, raw.copyEn, 'copy/en.json')

  if (foods) {
    const friends = foods.items.filter((food) => food.class === 'friend').length
    const risky = foods.items.filter((food) => food.class === 'risky').length
    if (foods.items.length !== 24)
      error('foods.json', `CONTENT §9 wants 24 foods, found ${foods.items.length}`)
    if (friends !== 12 || risky !== 12) {
      error('foods.json', `CONTENT §9 wants 12 friends and 12 risky, found ${friends} and ${risky}`)
    }
    assertUnique(
      foods.items.map((food) => food.id),
      'foods.json',
      error,
    )
  }

  if (habits) {
    if (habits.cards.length !== 18) {
      error('habits.json', `CONTENT §10 wants 18 cards, found ${habits.cards.length}`)
    }
    const yes = habits.cards.filter((card) => card.answer).length
    if (habits.cards.length === 18 && yes !== 9) {
      error('habits.json', `CONTENT §10 wants 9 doğru and 9 yanlış, found ${yes} doğru`)
    }
    assertUnique(
      habits.cards.map((card) => card.id),
      'habits.json',
      error,
    )
  }

  if (badges) {
    const badgeIds = badges.badges.map((badge) => badge.id)
    if (badgeIds.length !== BADGE_IDS.length || badgeIds.some((id, i) => id !== BADGE_IDS[i])) {
      error('badges.json', 'ids must match shared/src/ids.ts BADGE_IDS exactly, in order')
    }
  }

  if (shop) {
    assertUnique(
      shop.items.map((item) => item.id),
      'shop.json',
      error,
    )
    for (const item of shop.items) {
      if (item.source === 'shop' && item.price === null) {
        error('shop.json', `${item.id} is in the shop but has no price`)
      }
      if (item.source !== 'shop' && item.price !== null) {
        error('shop.json', `${item.id} is earned, so its price must be null`)
      }
      if (item.kind === 'cosmetic' && item.slot === undefined) {
        error('shop.json', `${item.id} is a cosmetic and needs a slot`)
      }
      if (item.kind !== 'cosmetic' && item.slot !== undefined) {
        error('shop.json', `${item.id} is not a cosmetic and must not carry a slot`)
      }
    }
    // MASTERPLAN §8.4 catalogue sizes, plus the six region rewards from CONTENT §2.
    const counts = {
      food: shop.items.filter((item) => item.kind === 'food' && item.source === 'shop').length,
      head: shopSlotCount(shop, 'head'),
      eyes: shopSlotCount(shop, 'eyes'),
      back: shopSlotCount(shop, 'back'),
      hand: shopSlotCount(shop, 'hand'),
      decor: shop.items.filter((item) => item.kind === 'decor' && item.source === 'shop').length,
      region: shop.items.filter((item) => item.source === 'region').length,
    }
    const expected = { food: 8, head: 10, eyes: 6, back: 6, hand: 6, decor: 12, region: 6 }
    for (const [key, want] of Object.entries(expected)) {
      const got = counts[key as keyof typeof counts]
      if (got !== want) error('shop.json', `MASTERPLAN §8.4 wants ${want} ${key}, found ${got}`)
    }

    const shopIds = new Set(shop.items.map((item) => item.id))
    if (foods) {
      for (const item of shop.items) {
        if (item.kind === 'food' && !foods.items.some((food) => food.id === item.id)) {
          error('shop.json', `food ${item.id} is not in foods.json`)
        }
      }
    }
    for (const lesson of lessons) {
      if (!shopIds.has(lesson.reward.cosmetic)) {
        error(
          `lessons/${lesson.id}`,
          `reward cosmetic ${lesson.reward.cosmetic} is not in shop.json`,
        )
      }
      if (badges && !badges.badges.some((badge) => badge.id === lesson.reward.badge)) {
        error(`lessons/${lesson.id}`, `reward badge ${lesson.reward.badge} is not in badges.json`)
      }
    }
  }

  if (raw.recap !== undefined) {
    const recap = parse(RecapSchema, raw.recap, 'recap.json')
    if (recap) checkRecap(recap, lessons, badges?.badges.map((badge) => badge.id) ?? [], error)
  }
  if (raw.challenges !== undefined) {
    const challenges = parse(ChallengesSchema, raw.challenges, 'challenges.json')
    if (challenges) checkPlaceholders(challenges, 'challenges.json', error)
  }
  for (const [file, value] of Object.entries(raw.daily ?? {})) {
    const daily = parse(DailySchema, value, `daily/${file}`)
    if (daily) checkDaily(daily, file, lessons, error)
  }

  for (const key of REQUIRED_COPY_KEYS) {
    if (typeof lookup(raw.copy, key) !== 'string') error('copy/tr.json', `missing key ${key}`)
  }
  for (const key of REQUIRED_PARENT_COPY_KEYS) {
    if (typeof lookup(raw.parentCopy, key) !== 'string') {
      error('copy/parent.tr.json', `missing key ${key}`)
    }
  }

  const turkishSources: [string, unknown][] = [
    ['copy/tr.json', raw.copy],
    ['copy/parent.tr.json', raw.parentCopy],
    ['foods.json', raw.foods],
    ['habits.json', raw.habits],
    ['badges.json', raw.badges],
    ['shop.json', raw.shop],
    ...Object.entries(raw.lessons).map(
      ([file, value]) => [`lessons/${file}`, value] as [string, unknown],
    ),
    ...(raw.recap !== undefined ? [['recap.json', raw.recap] as [string, unknown]] : []),
    ...(raw.challenges !== undefined
      ? [['challenges.json', raw.challenges] as [string, unknown]]
      : []),
    ...Object.entries(raw.daily ?? {}).map(
      ([file, value]) => [`daily/${file}`, value] as [string, unknown],
    ),
  ]
  for (const [where, value] of turkishSources) {
    walkStrings(value, '', (text, path) => {
      for (const problem of checkCopyString(text, path)) {
        error(where, `CONVENTIONS §4 at ${problem.path || '(root)'}: ${problem.why}`)
      }
    })
  }

  return issues
}

type ErrorSink = (where: string, message: string) => void

/** EDU-08: seven cards in lesson order, six questions one per lesson, none a lesson's prompt. */
function checkRecap(recap: Recap, lessons: Lesson[], badgeIds: string[], error: ErrorSink) {
  const where = 'recap.json'
  const [opener, ...rest] = recap.cards
  if (opener?.id !== 'c0' || opener.lessonId !== undefined) {
    error(where, 'card c0 is the opener and carries no lessonId')
  }
  rest.forEach((card, index) => {
    if (card.lessonId !== LESSON_IDS[index]) {
      error(where, `card ${card.id} must revisit ${LESSON_IDS[index]}`)
    }
  })
  recap.questions.forEach((question, index) => {
    const lessonId = LESSON_IDS[index]
    if (question.lessonId !== lessonId || question.id !== `h-${lessonId}`) {
      error(where, `question ${index + 1} must be h-${lessonId} for ${lessonId}`)
    }
    checkQuestionShape(question, `${where} ${question.id}`, error)
  })
  const lessonPrompts = new Set(
    lessons.flatMap((lesson) =>
      lesson.question ? [lesson.question.prompt.mini, lesson.question.prompt.core] : [],
    ),
  )
  for (const question of recap.questions) {
    for (const prompt of [question.prompt.mini, question.prompt.core]) {
      if (lessonPrompts.has(prompt)) {
        error(where, `EDU-08: ${question.id} repeats a lesson prompt: ${prompt}`)
      }
    }
  }
  if (badgeIds.length > 0 && !badgeIds.includes(recap.reward.badge)) {
    error(where, `reward badge ${recap.reward.badge} is not in badges.json`)
  }
}

function checkQuestionShape(
  question: { options: { id: string; correct: boolean }[]; miniOptions: string[] },
  where: string,
  error: ErrorSink,
) {
  const correct = question.options.filter((option) => option.correct)
  if (question.options.length !== 3 || correct.length !== 1) {
    error(where, `wants three options with exactly one correct, found ${correct.length} correct`)
  }
  const optionIds = new Set(question.options.map((option) => option.id))
  for (const miniOption of question.miniOptions) {
    if (!optionIds.has(miniOption))
      error(where, `miniOptions references unknown option ${miniOption}`)
  }
  if (!question.miniOptions.some((id) => question.options.find((o) => o.id === id)?.correct)) {
    error(where, 'miniOptions must include the correct option')
  }
}

/** `{{game}}` and `{{score}}` are the only placeholders a görev text may carry. */
function checkPlaceholders(value: unknown, where: string, error: ErrorSink) {
  walkStrings(value, '', (text, path) => {
    for (const match of text.matchAll(PLACEHOLDER_RE)) {
      if (!ALLOWED_PLACEHOLDERS.has(match[1] ?? '')) {
        error(where, `unknown placeholder ${match[0]} at ${path}`)
      }
    }
  })
}

/** W3-OVERHAUL §2.6: ids carry the file's date, one question per lesson, facts from the list. */
function checkDaily(daily: Daily, file: string, lessons: Lesson[], error: ErrorSink) {
  const where = `daily/${file}`
  if (file !== `${daily.date}.json`) error(where, `date ${daily.date} does not match the file name`)
  daily.questions.forEach((question, index) => {
    const lessonId = LESSON_IDS[index]
    const wantId = `d-${daily.date}-${index + 1}`
    if (question.id !== wantId) error(where, `question ${index + 1} must be ${wantId}`)
    if (question.lessonId !== lessonId) error(where, `${question.id} must revisit ${lessonId}`)
    if (FACT_LESSON[question.factId] !== lessonId) {
      error(where, `${question.id}: fact ${question.factId} belongs to another lesson`)
    }
    checkQuestionShape(question, `${where} ${question.id}`, error)
  })
  const lessonPrompts = new Set(
    lessons.flatMap((lesson) =>
      lesson.question ? [lesson.question.prompt.mini, lesson.question.prompt.core] : [],
    ),
  )
  for (const question of daily.questions) {
    for (const prompt of [question.prompt.mini, question.prompt.core]) {
      if (lessonPrompts.has(prompt)) error(where, `${question.id} repeats a lesson prompt`)
    }
  }
  if (daily.gorevTwist) checkPlaceholders(daily.gorevTwist, where, error)
}

function shopSlotCount(shop: Shop, slot: (typeof SLOTS)[number]): number {
  return shop.items.filter(
    (item) => item.kind === 'cosmetic' && item.slot === slot && item.source === 'shop',
  ).length
}

function assertUnique(ids: string[], where: string, error: (where: string, msg: string) => void) {
  const seen = new Set<string>()
  for (const id of ids) {
    if (seen.has(id)) error(where, `duplicate id ${id}`)
    seen.add(id)
  }
}

/** Builds the fold's catalog from the shop and the lesson rewards. */
export function catalogFromContent(shop: Shop, lessons: readonly Lesson[]): Catalog {
  const items: Catalog['items'] = {}
  for (const item of shop.items) {
    items[item.id] = {
      kind: item.kind,
      price: item.price,
      purchasable: item.source === 'shop',
      ...(item.slot ? { slot: item.slot } : {}),
    }
  }
  const lessonCosmetic: Partial<Record<LessonId, string>> = {}
  for (const lesson of lessons) lessonCosmetic[lesson.id] = lesson.reward.cosmetic
  return { items, lessonCosmetic }
}
